import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const empty = () => ({ supplierId: '', priceScore: 75, qualityScore: 75, deliveryScore: 75, serviceScore: 75, notes: '' });

const CRITERIA = [
  ['priceScore', 've.col.price'],
  ['qualityScore', 've.col.quality'],
  ['deliveryScore', 've.col.delivery'],
  ['serviceScore', 've.col.service'],
];

export function VendorEvaluationModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  const suppliers = useQuery({ queryKey: ['inventory', 'suppliers'], queryFn: () => inventoryApi.listSuppliers().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const overall = ((Number(form.priceScore) + Number(form.qualityScore) + Number(form.deliveryScore) + Number(form.serviceScore)) / 4).toFixed(2);

  const save = useMutation({
    mutationFn: (body) => inventoryApi.createSupplierEvaluation(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'supplier-ranking'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'supplier-evaluations'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.supplierId) { setError(t('ve.error.required')); return; }
    save.mutate({
      supplierId: form.supplierId,
      priceScore: Number(form.priceScore), qualityScore: Number(form.qualityScore),
      deliveryScore: Number(form.deliveryScore), serviceScore: Number(form.serviceScore),
      notes: form.notes || null,
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('ve.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field"><span className="field-label">{t('ve.col.supplier')}</span>
          <select className="input" value={form.supplierId} onChange={set('supplierId')} required>
            <option value="">-</option>
            {(suppliers.data || []).map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}
          </select>
        </label>
        {CRITERIA.map(([key, label]) => (
          <label key={key} className="field">
            <span className="field-label">{t(label)}: <strong className="mono">{form[key]}</strong></span>
            <input type="range" min="0" max="100" value={form[key]} onChange={set(key)} />
          </label>
        ))}
        <p className="muted">{t('ve.col.overall')}: <strong className="mono">{overall}</strong></p>
        <label className="field"><span className="field-label">{t('ve.field.notes')}</span>
          <input className="input" value={form.notes} onChange={set('notes')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
